import { CellStyle, ValueFormatterParams } from 'ag-grid-enterprise'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { isDisabledIdeaState } from '../enums/management-input.enum'
import { STAR_SCORE_MAX, STAR_SCORE_MIN } from './constants'

dayjs.extend(utc)
dayjs.extend(timezone)

export function checkStateColoredNumberCellStyle(params: {
  value: number
  data?: { state?: string }
}): CellStyle {
  return {
    color: getCheckStateNumberColor(params.value, params?.data?.state ?? ''),
    textAlign: 'right',
  } as CellStyle
}

export function coloredNumberCellStyle(params: { value: number }): CellStyle {
  return params.value !== 0
    ? { color: getNumberColor(params.value) ?? '', textAlign: 'right' }
    : ({ textAlign: 'right' } as CellStyle)
}

export function getCheckStateNumberColor(
  value: number,
  state: string
): string | undefined {
  return isDisabledIdeaState(state)
    ? 'var(--color-text-disabled)'
    : getNumberColor(value)
}

export function getNumberColor(value: number): string | undefined {
  if (value > 0) return 'var(--color-long-text)'
  if (value < 0) return 'var(--color-short-text)'
  return undefined
}

// Formats a 0-5 star score: numeric values render with 2 decimals, out-of-range
// (<0 or >5) show N/A, non-numeric values pass through unchanged.
export function formatStarScore(value: string | number): string {
  const num = typeof value === 'string' ? Number(value) : value
  if (value === '' || value == null || Number.isNaN(num))
    return String(value ?? 'None')
  if (num < STAR_SCORE_MIN || num > STAR_SCORE_MAX) return 'N/A'
  return num.toFixed(2)
}

export function numberCellStyle(): CellStyle {
  return { textAlign: 'right' } as CellStyle
}

export function percentageValueFormatter(
  params: ValueFormatterParams,
  options: { decimal?: number } = {}
) {
  const { value } = params
  const decimal = options.decimal ?? 2
  if (typeof value !== 'number' || isNaN(value)) return '-'
  return `${(value * 100).toFixed(decimal)}%`
}

export function currencyValueFormatter(
  params: ValueFormatterParams,
  options: { decimal?: number; currency?: string } = {}
): string {
  const { value, data } = params
  // Prefer options.currency, fallback to params.data?.tickerCurrency
  const currency = options.currency || data?.tickerCurrency|| data?.currency || ''
  const decimal = options.decimal ?? 2

  if (value === undefined || value === null || value === '') {
    return '-'
  }

  const num = typeof value === 'string' ? parseFloat(value) : value
  if (typeof num !== 'number' || isNaN(num)) {
    return '-'
  }

  if (currency) {
    const absNum = Math.abs(num)
    const formattedNumber = absNum.toLocaleString('en-US', {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    })

    let currencySymbol
    // Special case for japanese currency
    if (currency.toUpperCase() === 'JPY') {
      currencySymbol = 'JP¥'
    } else if (currency.toUpperCase() === 'SGD') {
      currencySymbol = 'S$'
    } else {
      currencySymbol =
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
        })
          .formatToParts(1)
          .find((part) => part.type === 'currency')?.value || currency
    }

    if (num < 0) {
      return `(${currencySymbol} ${formattedNumber})`
    } else {
      return `${currencySymbol} ${formattedNumber}`
    }
  }

  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimal,
    maximumFractionDigits: decimal,
  })
}

export function formatCurrency(
  value: number,
  options: { decimal?: number; currency?: string } = {}
): string {
  const mockParams = { value, data: {} } as ValueFormatterParams
  return currencyValueFormatter(mockParams, options)
}

export function formatPercentage(
  value: number,
  options: { decimal?: number } = {}
): string {
  const mockParams = { value } as ValueFormatterParams
  return percentageValueFormatter(mockParams, options)
}

export function marketDateTimezoneFormatter(
  params: {
    value: string | null | undefined
    data: { marketTimeZone: string }
  },
  options: {
    showTime?: boolean
    showTimezone?: boolean
    dateOnly?: boolean
    timeOnly?: boolean
  } = {
    showTime: true,
    showTimezone: false,
    dateOnly: false,
    timeOnly: false,
  }
) {
  if (!params.value) {
    return ''
  }

  const showTime = options.showTime ?? true

  const getTimezoneAbbreviation = (timezone: string, date: dayjs.Dayjs) => {
    // Determine DST by comparing the date's UTC offset against the timezone's
    // standard (non-DST) offset for THAT specific year. Build the January and
    // July instants from the date's own year via dayjs.tz(dateString, zone) so
    // each offset is recomputed correctly (calling .month() on an existing .tz()
    // object does NOT recompute the offset, which caused the previous bug).
    const year = date.year()
    const januaryOffset = dayjs.tz(`${year}-01-01`, timezone).utcOffset()
    const julyOffset = dayjs.tz(`${year}-07-01`, timezone).utcOffset()
    const currentOffset = date.utcOffset()

    // Standard time is always the smaller UTC offset across the year; DST is the
    // larger one. This holds for both hemispheres (southern-hemisphere DST falls
    // in January, not July), so avoid assuming July is always DST.
    const standardOffset = Math.min(januaryOffset, julyOffset)
    const isDST = januaryOffset !== julyOffset && currentOffset > standardOffset

    const timezoneMap: { [key: string]: { standard: string; dst: string } } = {
      'Asia/Hong_Kong': { standard: 'HKT', dst: 'HKT' },
      'Asia/Shanghai': { standard: 'CST', dst: 'CST' },
      'Asia/Tokyo': { standard: 'JST', dst: 'JST' },
      'America/New_York': { standard: 'EST', dst: 'EDT' },
      'Europe/London': { standard: 'GMT', dst: 'BST' },
      'Asia/Singapore': { standard: 'SGT', dst: 'SGT' },
      'Australia/Sydney': { standard: 'AEST', dst: 'AEDT' },
      'Asia/Seoul': { standard: 'KST', dst: 'KST' },
      'Asia/Taipei': { standard: 'CST', dst: 'CST' },
      'Asia/Kolkata': { standard: 'IST', dst: 'IST' },
    }

    const mapping = timezoneMap[timezone]
    if (!mapping) return timezone ?? ''

    return isDST ? mapping.dst : mapping.standard
  }

  const dayjsObj = dayjs(params.value).tz(params.data.marketTimeZone)

  if (options.dateOnly) {
    return dayjsObj.format('YYYY-MM-DD')
  }

  const abbreviation = getTimezoneAbbreviation(
    params.data.marketTimeZone,
    dayjsObj
  )

  if (options.timeOnly) {
    const formatted = dayjsObj.format('HH:mm:ss')
    return abbreviation ? `${formatted} ${abbreviation}` : formatted
  }

  if (options.showTimezone) {
    const formatted = dayjsObj.format(`YYYY-MM-DD HH:mm`)
    return abbreviation ? `${formatted} ${abbreviation}` : formatted
  }

  if (!showTime) {
    return dayjsObj.format(`YYYY-MM-DD`)
  }
  return dayjsObj.format(`YYYY-MM-DD HH:mm`)
}

function getDecimalPlaces(num: number) {
  if (Number.isInteger(num)) {
    return 0 // No decimal places for integers
  }
  const decimalPart = num.toString().split('.')[1]
  return decimalPart ? decimalPart.length : 0
}

/**
 * @deprecated Fixed to millions ("M") only. Prefer {@link compactNumberFormatter},
 * which auto-selects the K/M/B/T unit and keeps 3 significant digits. Kept for
 * the statistics table until its columns migrate.
 */
export function millionFormatter(
  params: { value: number },
  options: { decimal?: number; includeSymbol?: boolean } = {}
): string {
  if (!params.value) return ''
  const value = params.value / 1000000
  let decimal = getDecimalPlaces(value)
  const { decimal: optDecimal = 2, includeSymbol = true } = options
  if (decimal > optDecimal) {
    decimal = optDecimal
  }

  if (includeSymbol) {
    return `$ ${value.toFixed(decimal)}M`
  } else {
    return `${value.toFixed(decimal)}M`
  }
}

export function compactNumberFormatter(
  params: { value: number },
  options: {
    includeSymbol?: boolean
    currency?: string
    rounding?: 'round' | 'floor'
  } = {}
): string {
  const { value } = params
  if (value === null || value === undefined || isNaN(value)) return ''

  const { includeSymbol = false, currency, rounding = 'round' } = options
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  const prefix = currency ? `${currency} ` : includeSymbol ? '$ ' : ''

  if (abs === 0) {
    return `${sign}${prefix}0`
  }

  const units: [number, string][] = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
    [1, ''],
  ]

  let [divisor, suffix] = units[units.length - 1]
  for (const [unitValue, unitSuffix] of units) {
    if (abs >= unitValue) {
      divisor = unitValue
      suffix = unitSuffix
      break
    }
  }

  // 3 significant digits: 0 decimals for 100-999, 1 for 10-99, 2 for 1-9.
  const toDecimals = (n: number) => (n >= 100 ? 0 : n >= 10 ? 1 : 2)
  const apply = (n: number, d: number) => {
    const factor = 10 ** d
    // floor: +1e-9 absorbs float artifacts (e.g. 1.23 * 100 = 122.999...).
    return rounding === 'floor'
      ? Math.floor(n * factor + 1e-9) / factor
      : Math.round(n * factor) / factor
  }

  let scaled = abs / divisor
  let decimals = toDecimals(scaled)
  let display = apply(scaled, decimals)

  // Rounding can push to 1000 (e.g. 999999 -> "1000K") -> promote to the next
  // unit. Flooring never reaches 1000, so this branch only affects 'round'.
  if (display >= 1000) {
    const index = units.findIndex(([, unitSuffix]) => unitSuffix === suffix)
    if (index > 0) {
      divisor = units[index - 1][0]
      suffix = units[index - 1][1]
      scaled = abs / divisor
      decimals = toDecimals(scaled)
      display = apply(scaled, decimals)
    }
  }

  return `${sign}${prefix}${display.toFixed(decimals)}${suffix}`
}

/**
 * Picks a single magnitude unit (K/M/B/T) for an entire axis based on the
 * largest absolute value in `values`, so axis labels stay within 1~3 digits.
 *
 * @returns the unit `suffix` (e.g. 'K'), the `divisor` used to normalize raw
 * values, and a `labelFormatter` ready for an ECharts `axisLabel.formatter`.
 */
export function getAxisUnitScale(values: number[]): {
  suffix: string
  divisor: number
  labelFormatter: (value: number) => string
} {
  const maxAbsValue = values.reduce(
    (max, value) => Math.max(max, Math.abs(value)),
    0
  )

  const units: [number, string][] = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
    [1, ''],
  ]

  let divisor = 1
  let suffix = ''
  for (const [unitValue, unitSuffix] of units) {
    if (maxAbsValue >= unitValue) {
      divisor = unitValue
      suffix = unitSuffix
      break
    }
  }

  return {
    suffix,
    divisor,
    labelFormatter: (value: number): string =>
      `${parseFloat((value / divisor).toFixed(2))}`,
  }
}
